import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";
import type { User } from "../types";
import { Avatar } from "./ui/Avatar";
import { Popover } from "./ui/Popover";
import { AwayControls } from "./AwayControls";
import { PersonalSettingsDialog } from "./PersonalSettingsDialog";

/**
 * The signed-in agent, in the top right of every screen.
 *
 * One component for all four products because the block is about the *person*,
 * not the product: their avatar, their settings, their session. The Helpdesk,
 * the CRM, Sales calls and Meetings are four tabs of one platform to whoever is
 * using them, and an agent who connects their Lane account from the helpdesk
 * has connected it for the CRM too -- there is one account and one connection
 * behind all of them.
 *
 * That is also why "Your settings" is here rather than in any product's
 * Settings page. Theme, avatar, Lane, notifications and API keys follow the
 * person around; channels, pipelines and custom fields belong to a product and
 * stay on its own page.
 *
 * ## Why the top right rather than the foot of the sidebar
 *
 * This was a block at the bottom of each product's sidebar, with the name, the
 * address and two always-visible links under it. It was four copies of the
 * chrome in the same place Lane puts nothing, and it spent a fixed strip of the
 * navigation column on two links a person clicks a handful of times a month.
 *
 * The rail already says which app you are in and Lane -- the other app these
 * people have open all day -- puts the account in the top right, so that corner
 * is where a NetXL hand already looks for it. Collapsing it to an avatar behind
 * a dropdown is what makes the move an improvement rather than a relocation:
 * the sidebar gets its foot back, and "Sign out" stops sitting one stray click
 * away from the view list.
 */
export function UserMenu({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  /**
   * Sign out of Pulse, which is all "Sign out" here has ever honestly meant.
   *
   * A real request rather than a navigation, because everything it touches is
   * ours to touch. The `id` session stands -- somebody leaving Pulse has not
   * asked to be signed out of Lane -- and identify will not let Pulse back in
   * on it until the person has signed in again, so the standing session is not
   * a way to undo this by clicking "Sign in".
   */
  const signOut = async (): Promise<void> => {
    await api.logout();
    queryClient.clear();
    window.location.href = "/";
  };

  return (
    <>
      {/*
        Outside the popover, and rendered even once it has closed: opening the
        dialog dismisses the menu it was chosen from, and a dialog mounted
        inside that menu would unmount with it.
      */}
      {settingsOpen ? (
        <PersonalSettingsDialog
          user={user}
          onClose={() => setSettingsOpen(false)}
        />
      ) : null}

      <Popover
        open={open}
        onOpenChange={setOpen}
        label="Your account"
        /* A page header, so it opens downwards and hangs from its right edge --
           the trigger is the last thing in the bar and a left-aligned panel
           would run off the window. */
        side="bottom"
        align="right"
        className="w-60"
        trigger={
          /* The same avatar as everywhere else, so an agent is recognisable in
             the bar and in a list row without a second style. The ring appears
             on hover only: a permanent one would make the header's one circle
             read as selected. */
          <Avatar
            agent={{ id: user.id, name: user.name, color: user.avatarColor }}
            size="md"
            className="ring-2 ring-transparent transition-colors hover:ring-line"
          />
        }
      >
        {/*
          The name and address are in the panel rather than beside the avatar in
          the bar. An initials circle is not an identity -- two agents can share
          initials -- and this is the one place a person checks *which* account
          they are signed in as, which is worth a click of a menu they are
          already opening to reach the only two actions in it.
        */}
        <div className="flex items-center gap-2 px-3 py-2">
          <Avatar
            agent={{ id: user.id, name: user.name, color: user.avatarColor }}
            size="md"
          />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-content">
              {user.name}
            </p>
            <p className="truncate text-xs text-faint">{user.email}</p>
          </div>
        </div>

        <div className="my-1 border-t border-line" />

        {/*
          Above "Your settings", and behind its own rule, because it is the one
          thing here somebody opens this menu to *do* rather than to configure.
          Going to lunch is a daily act; changing your avatar is not.
        */}
        {/* Every entry here is a little taller on a phone (`max-md:py-2.5`):
            at a desktop's 32px a thumb aimed at "Your settings" lands on Sign
            out as often as not. */}
        <AwayControls onDone={() => setOpen(false)} />

        <div className="my-1 border-t border-line" />

        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setSettingsOpen(true);
          }}
          className="w-full px-3 py-1.5 text-left text-sm max-md:py-2.5 text-content hover:bg-surface-2"
        >
          Your settings
        </button>

        <div className="my-1 border-t border-line" />

        {/*
          Red, and behind its own rule. It is the only entry here that throws
          away what somebody is in the middle of, and it sits directly under a
          harmless one.

          One entry, because "sign out" here means one thing: leave Pulse. An
          agent who shares a desk with Lane open all day does not lose it to
          step away from this, and the next arrival here is still a real
          sign-in rather than a silent redirect -- identify holds that
          requirement, so this button does not have to explain it.

          Not phrased as "log out of all devices", because it is not one: this
          ends the session in this browser. Leaving everywhere is identify's
          own sign-out, on identify's own pages.
        */}
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            void signOut();
          }}
          className="w-full px-3 py-1.5 text-left text-sm max-md:py-2.5 text-red-600 hover:bg-surface-2 dark:text-red-400"
        >
          Sign out
        </button>
      </Popover>
    </>
  );
}
